import { BTPAR } from './binary-tree';
import { CoordinatorService } from './coordinator.service';

export type FieldType = 'string' | 'string[]' | 'longString' | 'number' | 'Date' | 'boolean';

export type TAGTEMPL = {
    tag: string;
    searchTag?: string;
    label?: string;
    type?: FieldType; // then assumed "string"
    isEditable?: boolean;
    inEditOnly?: boolean;
    hide?: boolean;
    mustHaveValue?: boolean;
};

export type TEMPL = {
    id: string;
    isGlobalEdit?: boolean;
    isLocalEdit?: boolean;
    pinId?: string;
    hideEmpty?: boolean;
    hideCSVExport?: boolean;
    tagLs: TAGTEMPL[];
};

export type CSSTYLE = {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
};

export type CSFPREFIX = '(' | 'doi: ' | 'url: ';
export type CSFCUTOFFSUFIX = 'etc.' | 'et al.';
export type CSFCUTOFFINFIX = '… ';
export type CSFROLE = 'authors' | 'title';
export type CSFLISTSEPARATOR = '; ' | ', ';
export type CSFSUFFIX = '. ' | ', ' | '; ' | ') ' | '] ' | '), ' | ': ' | '). ';
export type CSFSUFFIXIF = {
    char: '. ' | ', ' | '; ' | ') ' | '] ' | '' | '-';
    if: string;
};
export type CSFFUN = 'openDoi' | 'openUrl';

export type CSFPAR = {
    cutoff?: number; // used for lists, max number of items to quote
    cutoffInfix?: CSFCUTOFFINFIX; // if a list is abbreviated and the last item is displayed
    cutoffSuffix?: CSFCUTOFFSUFIX; // if a list is abbreviated
    fun?: CSFFUN;
    if?: string; // only if other field is present
    listSeparator?: CSFLISTSEPARATOR; // used for lists
    notInDetail?: boolean;
    prefix?: CSFPREFIX; // insert after a field
    role?: CSFROLE; // if not present, then role == tag; used to hide field in some context
    style?: CSSTYLE;
    suffix?: CSFSUFFIX; // insert after a field
    suffixHide?: string; // if the field ends with these characters, suffix is not used
    suffixIf?: CSFSUFFIXIF; // insert after a field if other present
    tag: string; // from biblioPatent/biblioArticle
};

export type CSPAR = {
    id: string;
    fieldLs: CSFPAR[];
};

export class TemplateRepository$ {
    constructor(private coords: CoordinatorService) {}

    get ratings(): BTPAR {
        return {
            meta: {
                title: 'Rating',
                type: 'ratings',
            },
            root: {
                meta: {
                    exclusiveChild: true,
                },
                childLs: [
                    {
                        meta: {
                            role: 'accepted',
                            title: 'accepted',
                        },
                    },
                    {
                        meta: {
                            role: 'postponed',
                            title: 'postponed',
                        },
                    },
                    {
                        meta: {
                            role: 'rejected',
                            title: 'rejected',
                        },
                    },
                ],
            },
        };
    }

    get tags(): BTPAR {
        return {
            meta: {
                title: 'Tags',
                type: 'tags',
            },
            root: {
                meta: {},
                childLs: [],
            },
        };
    }

    get projectDetail(): TEMPL {
        return {
            id: 'project-detail',
            isGlobalEdit: true,
            tagLs: [
                {
                    tag: 'title',
                    label: 'titleProject',
                    mustHaveValue: true,
                    //tady jsem změnila tag kvůli title v biblio, aby se to v překladu pro NTK netlouklo
                },
                {
                    tag: 'client',
                },
                {
                    tag: 'clientContact',
                },
                {
                    tag: 'ntkResearcher',
                },
                {
                    tag: 'startDate',
                },
                {
                    tag: 'deliveryDate',
                },
                // {
                //     tag: "keyWords",
                //     label: "keyWordsProject",
                // },
                {
                    tag: 'description',
                    label: 'descriptionProject',
                    type: 'longString',
                },
                {
                    tag: 'limitation',
                    type: 'longString',
                },
                {
                    tag: 'notes',
                    type: 'longString',
                },
                {
                    tag: 'tagLs',
                    type: 'string[]',
                },
            ],
        };
    }

    get citationStyleParams(): CSPAR[] {
        return [
            // Our own patent citation style
            {
                id: 'patent',
                fieldLs: [
                    {
                        tag: 'patentNumber',
                        suffix: ': ',
                        style: { bold: true },
                    },
                    {
                        tag: 'inventorLs',
                        role: 'authors',
                        listSeparator: '; ',
                        cutoff: 3,
                        cutoffSuffix: 'et al.',
                        suffix: '. ',
                        suffixHide: '.?!',
                    },
                    {
                        tag: 'title',
                        suffix: '. ',
                        suffixHide: '.?!',
                        notInDetail: true,
                    },
                    {
                        tag: 'applicantLs',
                        role: 'authors',
                        listSeparator: '; ',
                        cutoff: 3,
                        cutoffSuffix: 'et al.',
                        suffix: ', ',
                        suffixHide: '.?!',
                    },
                    {
                        tag: 'publicationDate', // formát data osekat, místo: Wed Jan 01 2020 00:00:00 GMT+0100 (Středoevropský standardní čas), nechat "Jan 01 2020"
                        suffix: '. ',
                    },
                ],
            },
            /* ACS patent citation style
            {
                id: "patent",
                fieldLs: [
                    {
                        tag: "inventorLs",
                        role: "authors",
                        listSeparator: "; ",
                        cutoff: 3,
                        cutoffSuffix: "et al.",
                        suffix: ". "
                        suffixHide: ".?!",
                    }, {
                        tag: "title",
                        suffix: ". ",
                    }, {
                        tag: "patentNumber",
                        prefix: "Patent ",
                        suffix: ", ",
                    }, {
                        tag: "publicationDate",
                        suffix: ". ",
                    },
                ]
            }, */

            {
                id: 'article',
                fieldLs: [
                    {
                        tag: 'authorLs',
                        role: 'authors',
                        listSeparator: '; ',
                        cutoff: 5,
                        cutoffInfix: '… ',
                        suffix: ': ',
                    },
                    {
                        tag: 'title',
                        suffix: '. ',
                        suffixHide: '.?!',
                        notInDetail: true,
                    },
                    {
                        tag: 'source',
                        suffix: ', ',
                    },
                    {
                        tag: 'volume',
                        suffix: ', ',
                        suffixIf: {
                            char: '',
                            if: 'issue',
                        },
                        style: { bold: true },
                    },
                    {
                        tag: 'issue',
                        prefix: '(',
                        suffix: '), ',
                        if: 'volume',
                    },
                    {
                        tag: 'startPage',
                        suffix: ', ',
                    },
                    {
                        tag: 'articleNumber',
                        suffix: ', ',
                        if: '!startPage',
                    },
                    {
                        tag: 'year',
                        suffix: '. ',
                    },
                    {
                        tag: 'doi',
                        prefix: 'doi: ',
                        fun: 'openDoi',
                        style: { italic: true, underline: true },
                    },
                    {
                        tag: 'url',
                        prefix: 'url: ',
                        fun: 'openUrl',
                        style: { italic: true, underline: true },
                    },
                ],
            },
            {
                id: 'article_APA',
                fieldLs: [
                    {
                        tag: 'authorLs',
                        role: 'authors',
                        listSeparator: ', ',
                        cutoff: 20,
                        cutoffInfix: '… ',
                        suffix: '. ',
                    },
                    {
                        tag: 'year',
                        prefix: '(',
                        suffix: '). ',
                    },
                    {
                        tag: 'title',
                        suffix: '. ',
                        suffixHide: '.?!',
                    },
                    {
                        tag: 'source',
                        style: { italic: true },
                        suffix: ', ',
                    },
                    {
                        tag: 'volume',
                        suffix: ', ',
                        suffixIf: {
                            char: '',
                            if: 'issue',
                        },
                        style: { italic: true },
                    },
                    {
                        tag: 'issue',
                        prefix: '(',
                        suffix: '), ',
                        if: 'volume',
                    },
                    {
                        tag: 'startPage',
                        suffix: '. ',
                        suffixIf: {
                            char: '-',
                            if: 'endPage',
                        },
                    },
                    {
                        tag: 'endPage',
                        suffix: '. ',
                    },
                    {
                        tag: 'doi',
                        fun: 'openDoi',
                        style: { underline: true },
                    },
                    {
                        tag: 'url',
                        fun: 'openUrl',
                        style: { underline: true },
                    },
                ],
            },
        ];
    }

    get keywordGroupStyleParams(): {
        color: string;
        background: string;
        isKeywordDefault?: boolean;
        isSearchDefault?: boolean;
    }[] {
        return [
            {
                color: '#FFFFFF',
                background: '#d50000',
                isKeywordDefault: true,
            },
            {
                color: '#FFFFFF',
                background: '#ff5252',
            },
            {
                color: '#FFFFFF',
                background: '#f44336',
            },
            {
                color: '#FFFFFF',
                background: '#c51162',
            },
            {
                color: '#FFFFFF',
                background: '#ff80ab',
            },
            {
                color: '#FFFFFF',
                background: '#e1bee7',
            },
            {
                color: '#FFFFFF',
                background: '#1565c0',
                isSearchDefault: true,
            },
            {
                color: '#FFFFFF',
                background: '#00838f',
            },
            {
                color: '#FFFFFF',
                background: '#00796b',
            },
            {
                color: '#FFFFFF',
                background: '#2e7d32',
            },
            {
                color: '#FFFFFF',
                background: '#827717',
            },
            {
                color: '#FFFFFF',
                background: '#5d4037',
            },
            {
                color: '#FFFFFF',
                background: '#757575',
            },
            {
                color: 'var(--default-dark)',
                background: '#ffcdd2',
            },
            {
                color: 'var(--default-dark)',
                background: '#f8bbd0',
            },
            {
                color: 'var(--default-dark)',
                background: '#ffcdd2',
            },
            {
                color: 'var(--default-dark)',
                background: '#ffcdd2',
            },
            {
                color: 'var(--default-dark)',
                background: '#bbdefb',
            },
            {
                color: 'var(--default-dark)',
                background: '#b2ebf2',
            },
            {
                color: 'var(--default-dark)',
                background: '#18ffff',
            },
            {
                color: 'var(--default-dark)',
                background: '#b2dfdb',
            },
            {
                color: 'var(--default-dark)',
                background: '#00e676',
            },
            {
                color: 'var(--default-dark)',
                background: '#c6ff00',
            },
            {
                color: 'var(--default-dark)',
                background: '#ffeb3b',
            },
            {
                color: 'var(--default-dark)',
                background: '#ffc400',
            },
        ];
    }

    get rssChannelLs() {
        return [
            {
                tag: 'nature',
                title: 'Nature',
                description: 'Nature',
                url: 'https://www.nature.com/nature.rss',
            },
            {
                tag: 'science',
                title: 'Science',
                description: 'Science',
                url: 'https://www.science.org/action/showFeed?type=etoc&feed=rss&jc=science',
            },
        ];
    }
}
